import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Radio,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { semanticColor, type SemanticRole } from "@how/design-system";
import { useOnChurchHost, useTenant } from "@how/app-kit";
import {
  ConfirmActionModal,
  EmptyState,
  ErrorState,
  LoadingState,
  useFocusWhenReady,
} from "@how/ui";
import { THEME_AVAILABILITY_LABELS, type ThemeAvailability } from "@how/website-core";
import {
  ThemeError,
  themeErrorMessage,
  useCreateThemePreview,
  usePlatformPublicHost,
  useSetSiteTheme,
  useSiteThemes,
  type SiteTheme,
} from "./theme-queries";

/*
 * The badge beside each theme's name. Premium is gold because premium distinction is exactly what the achievement role
 * is reserved for (UI_STANDARDS); a theme made for one church is the special role; an included theme stays neutral.
 */
const AVAILABILITY_ROLE: Record<ThemeAvailability, SemanticRole> = {
  included: "neutral",
  premium: "achievement",
  bespoke: "special",
};

/** Why a theme cannot be chosen, said beside it. */
function unavailableReason(theme: SiteTheme): string | null {
  if (theme.allowed) return null;
  return theme.availability === "premium"
    ? "Available on Pro and Elite"
    : "Not available to your church";
}

function isMissingHomePage(error: unknown): boolean {
  return error instanceof ThemeError && error.code === "P0002" && /home page/i.test(error.message);
}

export interface ThemeSectionProps {
  /** Whether the person may change the theme (settings.manage). Everyone who sees the Look page sees the list. */
  readonly editable: boolean;
}

/**
 * Website → Look → Theme (ADR 0050 section 2): the themes this church is offered, each with its availability; choose
 * one to see the church's own home page drawn in it, then Apply. A premium theme the church's plan does not include
 * is shown, disabled, with the plans that do. The database decides all of it again on Apply (`set_site_theme`).
 */
export function ThemeSection({ editable }: ThemeSectionProps) {
  const themes = useSiteThemes();
  const host = usePlatformPublicHost();
  // A church's own sign-in address may not frame the church's site (framing.ts lists HoW's dashboards only, A10). The
  // host flag is true before the church resolves, so it counts only once the church has.
  const churchHost = useOnChurchHost();
  const onChurchHost = useTenant().status === "resolved" && churchHost;
  const preview = useCreateThemePreview();
  const apply = useSetSiteTheme();
  const focusWhenReady = useFocusWhenReady();
  const [selected, setSelected] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const list = themes.data ?? [];
  const current = list.find((t) => t.isCurrent) ?? null;
  const chosenKey = selected ?? current?.key ?? null;
  const chosen = list.find((t) => t.key === chosenKey) ?? null;
  const choosingAnother = chosen !== null && current !== null && chosen.key !== current.key;
  const previewUrl =
    token === null || host.data === null || host.data === undefined
      ? null
      : `https://${host.data}/?preview=${encodeURIComponent(token)}`;

  const requestPreview = (key: string) => {
    setToken(null);
    preview.mutate(key, {
      onSuccess: (minted) => {
        setToken(minted);
      },
    });
  };

  const choose = (key: string) => {
    setSelected(key);
    setToken(null);
    preview.reset();
    if (editable && key !== current?.key) requestPreview(key);
  };

  const confirmApply = () => {
    if (chosen === null) return;
    const { key, name } = chosen;
    apply.mutate(key, {
      onSuccess: () => {
        setConfirming(false);
        setSelected(null);
        setToken(null);
        notifications.show({
          color: semanticColor("success"),
          title: "Theme applied",
          message: `Your website uses ${name} for every visitor.`,
        });
        // The Apply button leaves with the preview; focus goes to the theme that is now current.
        focusWhenReady(`[data-theme-card="${key}"][data-current="true"]`, {
          gone: "[data-theme-apply]",
        });
      },
      onError: (error) => {
        setConfirming(false);
        notifications.show({
          color: semanticColor("danger"),
          title: "Theme not applied",
          message: themeErrorMessage(error),
        });
      },
    });
  };

  return (
    <Card>
      <Stack gap="md">
        <div>
          <Title order={2} size="h4" id="theme-heading">
            Theme
          </Title>
          <Text size="sm" c="dimmed">
            A theme is the whole design of your website: its layout, type and spacing. Your palette,
            accent and logo carry into whichever theme you choose, and your pages stay exactly as
            you wrote them.
          </Text>
        </div>

        {themes.isPending ? (
          <LoadingState rows={2} label="Loading themes" />
        ) : themes.isError ? (
          <ErrorState
            message="Could not load the themes."
            onRetry={() => {
              void themes.refetch();
            }}
          />
        ) : list.length === 0 ? (
          <EmptyState
            title="No themes yet"
            description="Your website uses the standard theme. Other themes appear here as they become available."
          />
        ) : (
          <>
            <Radio.Group
              value={chosenKey}
              onChange={choose}
              aria-labelledby="theme-heading"
              name="site-theme"
            >
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="sm">
                {list.map((theme) => {
                  const reason = unavailableReason(theme);
                  const disabled = !editable || !theme.allowed;
                  return (
                    <Radio.Card
                      key={theme.key}
                      value={theme.key}
                      disabled={disabled}
                      radius="md"
                      p="md"
                      data-theme-card={theme.key}
                      data-current={theme.isCurrent ? "true" : "false"}
                      aria-label={`${theme.name}, ${THEME_AVAILABILITY_LABELS[theme.availability]}${
                        theme.isCurrent ? ", in use now" : ""
                      }${reason === null ? "" : `, ${reason}`}`}
                      style={{ cursor: disabled ? "not-allowed" : "pointer" }}
                    >
                      <Group wrap="nowrap" align="flex-start" gap="sm">
                        <Radio.Indicator disabled={disabled} aria-hidden />
                        <Stack gap={6} style={{ minWidth: 0, flex: 1 }}>
                          <Group gap="xs" wrap="wrap">
                            <Text fw={600} {...(disabled ? { c: "dimmed" } : {})}>
                              {theme.name}
                            </Text>
                            <Badge
                              variant="light"
                              color={semanticColor(AVAILABILITY_ROLE[theme.availability])}
                            >
                              {THEME_AVAILABILITY_LABELS[theme.availability]}
                            </Badge>
                          </Group>
                          {theme.isCurrent ? (
                            <Text size="sm" c="dimmed">
                              In use now
                            </Text>
                          ) : null}
                          {reason === null ? null : (
                            <Text size="sm" c="dimmed">
                              {reason}
                            </Text>
                          )}
                        </Stack>
                      </Group>
                    </Radio.Card>
                  );
                })}
              </SimpleGrid>
            </Radio.Group>

            {editable ? null : (
              <Text size="sm" c="dimmed">
                Someone who manages your church&apos;s settings can change the theme.
              </Text>
            )}

            {editable && choosingAnother ? (
              <Stack gap="sm" aria-live="polite">
                <Title order={3} size="h5">
                  Your home page in {chosen.name}
                </Title>
                {preview.isPending || host.isPending ? (
                  <Box role="status" aria-label="Preparing the preview">
                    <Skeleton height={480} radius="md" />
                  </Box>
                ) : preview.isError ? (
                  isMissingHomePage(preview.error) ? (
                    <Alert
                      variant="light"
                      color={semanticColor("info")}
                      title="Nothing to preview yet"
                    >
                      Your website has no home page yet, so there is nothing to show in{" "}
                      {chosen.name}. You can still apply it, and your pages will use it as you add
                      them.
                    </Alert>
                  ) : (
                    <Alert
                      variant="light"
                      color={semanticColor("danger")}
                      title="Could not prepare the preview"
                    >
                      <Stack gap="xs" align="flex-start">
                        <Text size="sm">{themeErrorMessage(preview.error)}</Text>
                        <Button
                          variant="default"
                          size="xs"
                          onClick={() => {
                            requestPreview(chosen.key);
                          }}
                        >
                          Try again
                        </Button>
                      </Stack>
                    </Alert>
                  )
                ) : previewUrl === null ? (
                  <Alert
                    variant="light"
                    color={semanticColor("warning")}
                    title="No website address"
                  >
                    Your website has no active address on HoW, so it cannot be previewed here.
                  </Alert>
                ) : onChurchHost ? (
                  <Alert variant="light" color={semanticColor("info")} title="Preview in a new tab">
                    <Stack gap="xs" align="flex-start">
                      <Text size="sm">
                        On your church's own address the preview opens in a new tab.
                      </Text>
                      <Anchor href={previewUrl} target="_blank" rel="noreferrer" size="sm">
                        Open the preview (opens in a new tab)
                      </Anchor>
                    </Stack>
                  </Alert>
                ) : (
                  <>
                    <Box
                      component="iframe"
                      src={previewUrl}
                      title={`Preview of your home page in the ${chosen.name} theme`}
                      // The public site runs no script; the frame may load its own styles and fonts and nothing else.
                      sandbox="allow-same-origin"
                      loading="lazy"
                      style={{
                        width: "100%",
                        height: "min(70vh, 640px)",
                        border: "1px solid var(--how-border-default)",
                        borderRadius: "var(--mantine-radius-md)",
                        background: "var(--how-surface-sunken)",
                        display: "block",
                      }}
                    />
                    <Anchor href={previewUrl} target="_blank" rel="noreferrer" size="sm">
                      Open the preview in a new tab (opens in a new tab)
                    </Anchor>
                  </>
                )}
                <Group gap="sm">
                  <Button
                    data-theme-apply
                    onClick={() => {
                      setConfirming(true);
                    }}
                    style={{ flexShrink: 0 }}
                  >
                    Apply {chosen.name}
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => {
                      const keep = current.key;
                      setSelected(null);
                      setToken(null);
                      preview.reset();
                      focusWhenReady(`[data-theme-card="${keep}"]`, { gone: "[data-theme-apply]" });
                    }}
                    style={{ flexShrink: 0 }}
                  >
                    Keep {current.name}
                  </Button>
                </Group>
              </Stack>
            ) : null}
          </>
        )}
      </Stack>

      <ConfirmActionModal
        opened={confirming && chosen !== null}
        onClose={() => {
          setConfirming(false);
        }}
        onConfirm={confirmApply}
        title={chosen === null ? "Apply theme" : `Apply ${chosen.name}?`}
        message={chosen === null ? "" : `Your website will use ${chosen.name} for every visitor.`}
        confirmLabel="Apply"
        loading={apply.isPending}
      />
    </Card>
  );
}
